import { useState } from "react";
import { Menu, X, ArrowRight, ArrowUpRight } from "lucide-react";
import { NetworkCanvas } from "../components/NetworkCanvas";
import { KeralaMap } from "../components/KeralaMap";
import { EnquiryForm, ContactAside } from "../components/EnquiryForm";
import { NAV_ITEMS, CAPABILITIES, SOLUTIONS, INDUSTRIES, DISTRICTS, LOGO } from "../data/content";

const img = (id, w = 1200) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=70`;

const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="fixed top-0 inset-x-0 z-40 bg-white/90 backdrop-blur-md border-b border-neutral-200">
      <div className="nx-wrap flex items-center justify-between h-16 sm:h-20">
        <a href="#top" className="flex items-center gap-3" data-testid="c2-logo">
          <img src={LOGO.iconLight} alt="NexSecure emblem" className="h-9 sm:h-11 w-auto" />
          <img src={LOGO.wordLight} alt="NexSecure Technologies" className="h-3.5 sm:h-4 w-auto" />
        </a>
        <nav className="hidden lg:flex items-center gap-7" data-testid="c2-nav">
          {NAV_ITEMS.map((n) => (
            <a key={n} href="#solutions" className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 hover:text-nx-gold transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-nx-gold hover:after:w-full after:transition-all">{n}</a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a href="#enquiry" data-testid="c2-header-cta" className="hidden sm:inline-block bg-nx-ink hover:bg-nx-gold text-white text-xs font-semibold tracking-[0.14em] uppercase px-5 py-3 transition-colors">Request a Site Assessment</a>
          <button className="lg:hidden text-nx-ink p-2" onClick={() => setOpen(!open)} data-testid="c2-mobile-menu-button" aria-label="Menu">{open ? <X size={22} /> : <Menu size={22} />}</button>
        </div>
      </div>
      {open && (
        <nav className="lg:hidden border-t border-neutral-200 bg-white px-6 py-4 flex flex-col gap-4" data-testid="c2-mobile-nav">
          {NAV_ITEMS.map((n) => <a key={n} href="#solutions" onClick={() => setOpen(false)} className="text-xs tracking-[0.22em] uppercase text-neutral-600">{n}</a>)}
          <a href="#enquiry" onClick={() => setOpen(false)} className="text-nx-gold text-xs tracking-[0.22em] uppercase font-semibold">Request a Site Assessment</a>
        </nav>
      )}
    </header>
  );
};

const Ticker = () => (
  <div className="border-y border-neutral-200 py-4 overflow-hidden" data-testid="c2-ticker">
    <div className="flex whitespace-nowrap animate-nx-marquee gap-0">
      {[0, 1].map((k) => (
        <div key={k} className="flex shrink-0">
          {[...CAPABILITIES.map((c) => c.title), ...INDUSTRIES.slice(0, 4)].map((t) => (
            <span key={`${k}-${t}`} className="flex items-center text-[11px] tracking-[0.3em] uppercase text-neutral-400">
              <span className="px-6">{t}</span><span className="text-nx-gold">·</span>
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const Hero = () => (
  <section id="top" className="bg-white pt-28 sm:pt-36 pb-16 sm:pb-24" data-testid="c2-hero">
    <div className="nx-wrap">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <div className="flex items-center gap-4 mb-8" data-testid="c2-hero-eyebrow">
            <span className="h-px w-12 bg-nx-gold" />
            <span className="text-[11px] sm:text-xs tracking-[0.3em] uppercase text-nx-gold font-semibold">Technology Infrastructure & Integrated Solutions</span>
          </div>
          <h1 className="font-archivo font-800 text-4xl sm:text-5xl lg:text-6xl text-nx-ink uppercase leading-[1.02] tracking-tight" data-testid="c2-hero-title" style={{ fontWeight: 800 }}>
            Engineering <span className="text-nx-gold">Intelligent</span> Infrastructure
          </h1>
          <p className="mt-7 text-base md:text-lg text-neutral-500 max-w-lg leading-relaxed" data-testid="c2-hero-subtitle">
            Software Services, Integrated Solutions, Smart Infrastructure, Automation and Managed Services — architected for the buildings, businesses and communities of Kerala.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a href="#enquiry" data-testid="c2-hero-primary-cta" className="group inline-flex items-center justify-center gap-3 bg-nx-ink hover:bg-nx-gold text-white font-semibold text-sm tracking-[0.08em] uppercase px-9 py-4 transition-colors duration-300">
              Request a Site Assessment <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a href="#solutions" data-testid="c2-hero-secondary-cta" className="inline-flex items-center justify-center border border-nx-ink/30 hover:border-nx-gold text-nx-ink text-sm tracking-[0.08em] uppercase px-9 py-4 transition-colors duration-300">Explore Solutions</a>
          </div>
        </div>
        <div className="relative" data-testid="c2-hero-visual">
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-7 border border-neutral-200 p-2">
              <img src={img("photo-1486406146926-c627a92ad1ab", 900)} alt="Modern glass corporate building facade" className="w-full h-56 sm:h-72 object-cover" loading="eager" />
            </div>
            <div className="col-span-5 mt-10 border border-neutral-200 p-2">
              <img src={img("photo-1487958449943-2429e8be8625", 700)} alt="White concrete architectural structure" className="w-full h-44 sm:h-56 object-cover" loading="lazy" />
            </div>
          </div>
          <div className="relative -mt-16 ml-auto w-4/5 sm:w-3/5 h-40 sm:h-48 border border-nx-gold/40 bg-nx-navy overflow-hidden">
            <NetworkCanvas skyline={false} className="absolute inset-0 w-full h-full" />
            <span className="absolute bottom-3 left-4 text-[10px] tracking-[0.28em] uppercase text-nx-goldlight">Live Infrastructure Mesh</span>
          </div>
        </div>
      </div>
    </div>
    <div className="mt-16 sm:mt-20"><Ticker /></div>
  </section>
);

const SolutionsIndex = () => (
  <section id="solutions" className="bg-white py-20 sm:py-28" data-testid="c2-solutions">
    <div className="nx-wrap">
      <div className="grid lg:grid-cols-[1fr_2fr] gap-12">
        <div>
          <span className="text-[11px] tracking-[0.3em] uppercase text-nx-gold font-semibold">Solutions Index</span>
          <h2 className="font-archivo text-3xl sm:text-4xl text-nx-ink uppercase tracking-tight mt-4" style={{ fontWeight: 800 }}>Ten areas.<br />One architecture.</h2>
          <p className="text-base text-neutral-500 mt-5 leading-relaxed max-w-sm">Security, networks, communication, automation and IT — specified together, delivered together, supported together.</p>
          <div className="mt-8 border border-neutral-200 p-2 hidden lg:block">
            <img src={img("photo-1449157291145-7efd050a4d0e", 700)} alt="Geometric building facade" className="w-full h-52 object-cover" loading="lazy" />
          </div>
        </div>
        <div className="border-t border-nx-ink/15">
          {SOLUTIONS.map((s, i) => (
            <a key={s.name} href="#enquiry" className="group grid grid-cols-[3rem_1fr_auto] sm:grid-cols-[4rem_1.2fr_1.6fr_auto] items-baseline gap-4 py-5 border-b border-nx-ink/15 hover:bg-nx-cream px-2 sm:px-4 transition-colors" data-testid={`c2-solution-${i}`}>
              <span className="font-archivo text-nx-gold text-sm" style={{ fontWeight: 700 }}>{String(i + 1).padStart(2, "0")}</span>
              <span className="font-archivo text-nx-ink text-base sm:text-lg uppercase tracking-tight" style={{ fontWeight: 700 }}>{s.name}</span>
              <span className="hidden sm:block text-sm text-neutral-500 leading-snug">{s.text}</span>
              <ArrowUpRight size={18} className="text-neutral-300 group-hover:text-nx-gold transition-colors" />
            </a>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Coverage = () => (
  <section className="bg-nx-cream py-20 sm:py-28" data-testid="c2-coverage">
    <div className="nx-wrap">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <div className="order-2 lg:order-1">
          <span className="text-[11px] tracking-[0.3em] uppercase text-nx-gold font-semibold">Kerala Service Coverage</span>
          <h2 className="font-archivo text-3xl sm:text-4xl text-nx-ink uppercase tracking-tight mt-4" style={{ fontWeight: 800 }}>On-site assessments, all fourteen districts.</h2>
          <p className="text-base text-neutral-500 mt-5 leading-relaxed">Businesses, institutions, industries, government organisations and communities — assessed on site, anywhere in Kerala, from our Ernakulam base.</p>
          <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2" data-testid="c2-district-list">
            {DISTRICTS.map((d) => (
              <span key={d} className="flex items-center gap-2 text-xs tracking-wider uppercase text-neutral-600">
                <span className="w-1 h-1 bg-nx-gold" /> {d}
              </span>
            ))}
          </div>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a href="#enquiry" data-testid="c2-coverage-primary-cta" className="inline-flex items-center justify-center gap-3 bg-nx-gold hover:bg-nx-goldlight text-white font-semibold text-sm tracking-[0.08em] uppercase px-9 py-4 transition-colors duration-300">
              Request a Site Assessment <ArrowRight size={16} />
            </a>
            <a href="#solutions" data-testid="c2-coverage-secondary-cta" className="inline-flex items-center justify-center border border-nx-ink/30 hover:border-nx-gold text-nx-ink text-sm tracking-[0.08em] uppercase px-9 py-4 transition-colors">Explore Solutions</a>
          </div>
        </div>
        <div className="order-1 lg:order-2 flex justify-center">
          <div className="bg-white border border-neutral-200 p-8 sm:p-12">
            <KeralaMap tone="light" className="h-[340px] sm:h-[420px] w-auto" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Enquiry = () => (
  <section id="enquiry" className="bg-nx-ink py-20 sm:py-28" data-testid="c2-enquiry">
    <div className="nx-wrap">
      <div className="grid lg:grid-cols-[1fr_1.6fr] gap-14">
        <div>
          <span className="text-[11px] tracking-[0.3em] uppercase text-nx-goldlight">Start a Conversation</span>
          <h2 className="font-archivo text-3xl sm:text-4xl text-white uppercase tracking-tight mt-4 mb-6" style={{ fontWeight: 800 }}>Request a consultation.</h2>
          <p className="text-base text-slate-400 leading-relaxed mb-10 max-w-sm">One solution area, one short message. Our engineers handle the rest of the discovery.</p>
          <ContactAside tone="dark" />
        </div>
        <div className="bg-white/[0.03] border border-white/10 p-7 sm:p-10">
          <EnquiryForm tone="dark" source="concept-2" />
        </div>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="bg-white border-t border-neutral-200 py-12 pb-28" data-testid="c2-footer">
    <div className="nx-wrap flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
      <div className="flex items-center gap-3">
        <img src={LOGO.iconLight} alt="" className="h-10 w-auto" />
        <img src={LOGO.wordLight} alt="NexSecure Technologies" className="h-4 w-auto" />
      </div>
      <nav className="flex flex-wrap gap-x-6 gap-y-2">
        {NAV_ITEMS.map((n) => <a key={n} href="#top" className="text-[11px] tracking-[0.2em] uppercase text-neutral-400 hover:text-nx-gold transition-colors">{n}</a>)}
      </nav>
      <p className="text-xs text-neutral-400">Ernakulam, Kerala · Engineering Intelligent Infrastructure</p>
    </div>
  </footer>
);

export default function ConceptTwo() {
  return (
    <div className="font-archivo bg-white text-nx-ink antialiased">
      <Header />
      <Hero />
      <SolutionsIndex />
      <Coverage />
      <Enquiry />
      <Footer />
    </div>
  );
}
